import React, { useState, useMemo, useRef } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Box,
  Grid,
  Paper,
  useTheme,
  alpha,
  Fab,
  Zoom,
  useScrollTrigger,
  useMediaQuery,
  Button,
  IconButton,
} from '@mui/material';
import { KeyboardArrowUp, Code, Work, Psychology, Terminal, KeyboardArrowDown, LinkedIn } from '@mui/icons-material';
import FeatureSummary from './components/FeatureSummary';
import FeatureFull from './components/FeatureFull';
import FilterBar from './components/FilterBar';
import HeroSection from './components/HeroSection';
import projectsData from './data/projects.json';

function ScrollTop({ children }) {
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Zoom in={trigger}>
      <Box
        onClick={handleClick}
        role="presentation"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
      >
        {children}
      </Box>
    </Zoom>
  );
}

function App() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [showProjects, setShowProjects] = useState(false);
  const projectsRef = useRef(null);

  const categories = ['All', 'Enterprise', 'Independent', 'Prototype'];
  
  const allTags = useMemo(() => {
    const tags = new Set();
    projectsData.forEach(project => {
      project.tags.forEach(tag => tags.add(tag));
    });
    return Array.from(tags).sort();
  }, []);

  const filteredFeatures = useMemo(() => {
    return projectsData.filter(project => {
      const categoryMatch = selectedCategory === 'All' || project.category === selectedCategory;
      const tagMatch = selectedTags.length === 0 || 
        selectedTags.some(tag => project.tags.includes(tag));
      return categoryMatch && tagMatch;
    });
  }, [selectedCategory, selectedTags]);

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Enterprise':
        return <Work sx={{ fontSize: 20 }} />;
      case 'Independent':
        return <Code sx={{ fontSize: 20 }} />;
      case 'Prototype':
        return <Psychology sx={{ fontSize: 20 }} />;
      default:
        return null;
    }
  };

  const getCategoryStats = () => {
    const stats = {
      Enterprise: projectsData.filter(p => p.category === 'Enterprise').length,
      Independent: projectsData.filter(p => p.category === 'Independent').length,
      Prototype: projectsData.filter(p => p.category === 'Prototype').length,
    };
    return stats;
  };

  const handleShowProjects = () => {
    setShowProjects(true);
    setTimeout(() => {
      if (projectsRef.current) {
        projectsRef.current.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 150);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar 
        position="sticky" 
        elevation={0}
        sx={{ 
          background: `
            linear-gradient(145deg, 
              ${alpha(theme.palette.background.paper, 0.95)}, 
              ${alpha(theme.palette.background.default, 0.95)}
            )
          `,
          backdropFilter: 'blur(20px)',
          borderBottom: `1px solid ${alpha('#00ffff', 0.2)}`,
          boxShadow: `0 8px 32px ${alpha('#00ffff', 0.1)}`,
        }}
      >
        <Toolbar sx={{ 
          minHeight: isMobile ? '64px !important' : '80px !important',
          px: { xs: 2, sm: 3 }
        }}>
          <Typography 
            variant={isSmallMobile ? "h6" : "h5"}
            component="div" 
            sx={{ 
              flexGrow: 1, 
              fontWeight: 700,
              color: '#00ffff',
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.5, sm: 1 },
              fontFamily: '"Fira Code", monospace',
              textShadow: '0 0 10px rgba(0, 255, 255, 0.5)',
            }}
            className="glow-text"
          >
            <Terminal sx={{ fontSize: { xs: 20, sm: 28 } }} />
            <Box component="span" sx={{ color: '#00ff41', fontSize: { xs: '0.9em', sm: '1em' } }}>
              {isSmallMobile ? 'ean' : 'ean@portfolio'}
            </Box>
            {!isSmallMobile && (
              <>
                <Box component="span" sx={{ color: '#ffab00' }}>:</Box>
                <Box component="span" sx={{ color: '#ff6b35' }}>~$</Box>
              </>
            )}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
            {!showProjects && (
              <Button
                variant="outlined"
                onClick={handleShowProjects}
                sx={{
                  color: '#00ffff',
                  borderColor: '#00ffff',
                  fontFamily: '"Fira Code", monospace',
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  px: { xs: 1.5, sm: 2 },
                  '&:hover': {
                    borderColor: '#00ff41',
                    color: '#00ff41',
                    backgroundColor: alpha('#00ff41', 0.1),
                  },
                }}
              >
                $ ls projects
              </Button>
            )}
            <IconButton
              component="a"
              href="https://www.linkedin.com/in/ean-nugent-67226732/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: '#0077b5',
                '&:hover': {
                  color: '#00ffff',
                  transform: 'scale(1.1)',
                  filter: 'drop-shadow(0 0 8px rgba(0, 255, 255, 0.6))',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <LinkedIn sx={{ fontSize: { xs: '1.5rem', sm: '1.8rem' } }} />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <HeroSection stats={getCategoryStats()} />

      {!showProjects && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          py: { xs: 4, sm: 6 },
          position: 'relative'
        }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleShowProjects}
            endIcon={<KeyboardArrowDown />}
            sx={{
              background: 'linear-gradient(145deg, #00ffff, #00b3b3)',
              color: '#000',
              fontFamily: '"Fira Code", monospace',
              fontWeight: 600,
              px: { xs: 3, sm: 4 },
              py: { xs: 1.5, sm: 2 },
              fontSize: { xs: '0.9rem', sm: '1rem' },
              border: `1px solid ${alpha('#00ffff', 0.5)}`,
              boxShadow: `0 8px 25px ${alpha('#00ffff', 0.3)}`,
              '&:hover': {
                background: 'linear-gradient(145deg, #00b3b3, #008080)',
                boxShadow: `0 12px 35px ${alpha('#00ffff', 0.4)}`,
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            Explore Projects
          </Button>
        </Box>
      )}

      {showProjects && (
        <Container
          ref={projectsRef}
          maxWidth="xl" 
          sx={{ py: { xs: 3, sm: 6 }, px: { xs: 2, sm: 3 } }}
        >
          <Box>
            <FilterBar
              categories={categories}
              selectedCategory={selectedCategory}
              onCategoryChange={setSelectedCategory}
              allTags={allTags}
              selectedTags={selectedTags}
              onTagsChange={setSelectedTags}
            />
          </Box>

          <Box sx={{ mt: { xs: 3, sm: 4 } }}>
            <Box sx={{ mb: { xs: 3, sm: 4 }, position: 'relative' }}>
              <Typography 
                variant="h4" 
                sx={{ 
                  mb: 1, 
                  fontWeight: 600,
                  color: '#00ff41',
                  fontFamily: '"Fira Code", monospace',
                  fontSize: { xs: '1.5rem', sm: '2.125rem' },
                }}
              >
                {selectedCategory === 'All' ? 'All Projects' : selectedCategory}
              </Typography>
              <Typography 
                component="span" 
                variant="h6" 
                sx={{ 
                  color: '#ffab00',
                  fontFamily: '"Fira Code", monospace',
                  opacity: 0.8,
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                }}
              >
                Found {filteredFeatures.length} entr{filteredFeatures.length !== 1 ? 'ies' : 'y'}
              </Typography>
            </Box>

            <Grid container spacing={{ xs: 2, sm: 3 }}>
              {filteredFeatures.map((feature, index) => (
                <Grid item xs={12} sm={6} lg={4} key={feature.id}>
                  <Box
                    sx={{
                      opacity: 0,
                      animation: `slideInUp 0.6s ease-out ${index * 0.1}s forwards`,
                      '@keyframes slideInUp': {
                        from: {
                          opacity: 0,
                          transform: 'translateY(30px)',
                        },
                        to: {
                          opacity: 1,
                          transform: 'translateY(0)',
                        },
                      },
                    }}
                  >
                    <FeatureSummary 
                      feature={feature} 
                      onClick={() => setSelectedFeature(feature)}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>

            {filteredFeatures.length === 0 && (
              <Paper 
                className="terminal-window"
                sx={{ 
                  p: { xs: 4, sm: 6 }, 
                  textAlign: 'center',
                  background: `
                    linear-gradient(145deg, #0a0a0a, #111111),
                    radial-gradient(circle at center, ${alpha('#ff6b35', 0.1)} 0%, transparent 70%)
                  `,
                  border: `1px dashed ${alpha('#ff6b35', 0.5)}`,
                  position: 'relative',
                  '&::before': {
                    content: '"Error 404:"',
                    position: 'absolute',
                    top: 8,
                    left: 12,
                    color: '#ff6b35',
                    fontFamily: '"Fira Code", monospace',
                    fontSize: '0.7rem',
                  },
                }}
              >
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: '#ff6b35',
                    fontFamily: '"Fira Code", monospace',
                    mb: 2,
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                  }}
                >
                  No projects found in directory
                </Typography>
                <Typography 
                  sx={{ 
                    color: 'text.secondary',
                    fontFamily: '"Inter", sans-serif',
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                  }}
                >
                  Try adjusting your filters or use --help for available options.
                </Typography>
              </Paper>
            )}
          </Box>
        </Container>
      )}

      <FeatureFull
        feature={selectedFeature}
        open={!!selectedFeature}
        onClose={() => setSelectedFeature(null)}
      />

      <ScrollTop>
        <Fab 
          size={isMobile ? "medium" : "small"}
          aria-label="scroll back to top"
          sx={{
            background: 'linear-gradient(145deg, #00ffff, #00b3b3)',
            color: '#000',
            border: `1px solid ${alpha('#00ffff', 0.5)}`,
            boxShadow: `0 8px 25px ${alpha('#00ffff', 0.3)}`,
            '&:hover': {
              background: 'linear-gradient(145deg, #00b3b3, #008080)',
              boxShadow: `0 12px 35px ${alpha('#00ffff', 0.4)}`,
              transform: 'scale(1.1)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          <KeyboardArrowUp />
        </Fab>
      </ScrollTop>
    </Box>
  );
}

export default App; 